import { dayKey } from "../format.js";

export type AvailabilityRange = "day" | "week";

export const availabilityRangeDays: Record<AvailabilityRange, number> = { day: 1, week: 7 };

/** Add calendar days to a YYYY-MM-DD key without consulting the viewer's timezone. */
export function availabilityDateOffset(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function availabilityWindow(startDate: string, range: AvailabilityRange) {
  return {
    startDate,
    endDate: availabilityDateOffset(startDate, availabilityRangeDays[range]),
  };
}

export function moveAvailabilityWindow(
  startDate: string,
  range: AvailabilityRange,
  direction: -1 | 1,
): string {
  return availabilityDateOffset(startDate, direction * availabilityRangeDays[range]);
}

export function fourWeeksFrom(startDate: string): string {
  return availabilityDateOffset(startDate, 28);
}

/** Lesson availability uses half-open intervals, so adjacent lessons do not overlap. */
export function lessonIntervalsOverlap(
  left: { starts_at: string; ends_at: string },
  right: { starts_at: string; ends_at: string },
): boolean {
  const leftStart = Date.parse(left.starts_at);
  const leftEnd = Date.parse(left.ends_at);
  const rightStart = Date.parse(right.starts_at);
  const rightEnd = Date.parse(right.ends_at);
  return Number.isFinite(leftStart) && Number.isFinite(leftEnd) &&
    Number.isFinite(rightStart) && Number.isFinite(rightEnd) &&
    leftStart < leftEnd && rightStart < rightEnd &&
    leftStart < rightEnd && rightStart < leftEnd;
}

/** Preserve day order while making each day's slots easy to render together. */
export function groupAvailabilitySlots<T extends { starts_at: string }>(slots: readonly T[]) {
  const groups = new Map<string, T[]>();
  for (const slot of [...slots].sort((a, b) => {
    const left = Date.parse(a.starts_at);
    const right = Date.parse(b.starts_at);
    return Number.isFinite(left) && Number.isFinite(right)
      ? left - right
      : a.starts_at.localeCompare(b.starts_at);
  })) {
    const key = dayKey(slot.starts_at);
    const daySlots = groups.get(key) ?? [];
    daySlots.push(slot);
    groups.set(key, daySlots);
  }
  return [...groups.entries()];
}
